import { useEffect, type RefObject } from 'react';

// Elements with an explicit tabindex="-1" are excluded even though some of
// them (e.g. button:not([disabled])) would otherwise match — react-day-picker
// gives every day cell a roving tabindex (0 on the one "focused" day, -1 on
// the rest), and a real browser's Tab key skips tabindex="-1" regardless of
// tag name.
const FOCUSABLE_SELECTOR = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// Traps Tab / Shift+Tab within `panelRef` while `active`. Deliberately does
// NOT filter by visibility (offsetParent etc.) — jsdom never runs layout, so
// that check would silently empty the list in every test (OH-122 precedent).
//
// The focusable set is queried INSIDE the keydown handler, not once when the
// panel opens: react-day-picker's grid uses a roving tabindex, so which day
// button is tabbable (tabindex="0") changes every time arrow-key navigation
// moves the "focused" day. A list captured on open would go stale the first
// time the guest presses an arrow key.
export function useFocusTrap(panelRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;

      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      const activeEl = document.activeElement;
      const outside = !(activeEl instanceof Node) || !panel.contains(activeEl);

      if (e.shiftKey) {
        if (outside || activeEl === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (outside || activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [active, panelRef]);
}
